import React from 'react';

import SearchableMenu, {
  TItemToString,
} from 'components/base/Menu/SearchableMenu';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import CustomerIcon from '../../CustomerIcon/CustomerIcon';
import colors from 'styles/themes/colors';
import { ThemeProps } from 'types/styled';
import styled, { css } from 'styled-components/macro';
import variables from 'styles/themes/variables';
import Checkbox from 'components/base/Checkbox/Checkbox';
import DropdownButton from '../DropdownButton';
import DropdownWrapper from '../DropdownWrapper';
import { ListLabelSpan } from '../shared/styles';

const hoveredStyles = (isActive: any): any => css`
  background: ${colors.Sand};
  span {
    transform: ${!isActive && 'translateX(5px)'};
  }
`;

const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered, disabled }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${!disabled && hoveredStyles(isActive)}
    }

    ${!disabled && isHovered && hoveredStyles(isActive)}
  `}
`;

export const ListItem: React.FC<IListItemProps> = (props) => {
  const {
    item: { name, label },
    isActive,
    isHovered,
    disabled,
  } = props;

  return (
    <ListItemBox
      isActive={false}
      isHovered={isHovered}
      disabled={disabled}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      py={2}
      paddingRight={2}
      paddingLeft={4}
    >
      <Box
        alignSelf="center"
        display="inline-block"
        marginLeft="2px"
        marginRight="-2px"
        opacity={disabled ? '0.5' : '1'}
      >
        <Checkbox
          enabledText="Disable"
          disabledText="Enable"
          isChecked={!!isActive}
          insideDropdown
        />
      </Box>
      <Box
        alignSelf="auto"
        display="inline-block"
        minWidth="2rem"
        maxHeight="1rem"
      >
        {name && <CustomerIcon small aria-hidden customer={name} />}
      </Box>
      <Span marginLeft={1} alignSelf="auto">
        {label}
      </Span>
    </ListItemBox>
  );
};

const getButtonWithDefaultLabel = (
  clearFilter: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        dropdownType="Genre"
        defaultLabel={defaultLabel}
        clearFilter={clearFilter}
        selectedItem={
          props.selectedItems.length === 1
            ? { displayName: props.selectedItems[0] }
            : undefined
        }
      />
    );
  };
  return LabeledButton;
};

export const GenreDropdown: React.FC<IGenreDropdownProps> = (props) => {
  const {
    items,
    selectedItems,
    onChange,
    disabled,
    disabledMessage,
    itemToString,
    defaultLabel,
    children,
    clearFilter,
  } = props;
  const selectedItemsNames = selectedItems.map((item: any) => item.displayName);
  const itemsWithoutSelected = items.filter(
    (item: any) => !selectedItemsNames.includes(item.displayName)
  );
  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.

  return (
    <DropdownWrapper label="GENRE">
      <SearchableMenu
        items={itemsWithoutSelected}
        selectedItems={selectedItemsNames || null}
        multipleSelections={selectedItems}
        onChange={onChange}
        disabled={disabled}
        disabledMessage={disabledMessage}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={ListItem}
        ListLabel={ListLabelSpan}
        isMultipleSelection
      >
        {children}
      </SearchableMenu>
    </DropdownWrapper>
  );
};

interface IGenreDropdownProps {
  items: any[];
  selectedItems: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  disabledMessage?: string;
  itemToString?: TItemToString;
  defaultLabel?: string;
  children?: any;
  clearFilter: () => void;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
  disabled?: boolean;
}
